import { useEffect, useState } from 'react';
import { api } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Download, Mail, FileSpreadsheet, Search, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import * as XLSX from 'xlsx';

export default function Carriers({ user }) {
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [formData, setFormData] = useState({
    legal_name: '', dba: '', mc_number: '', dot_number: '', ein: '',
    contact_name: '', phone: '', email: '', physical_address: '',
    equipment_types: [], max_weight: 0, home_state: '',
    preferred_lanes: '', factoring_company: '', notes: '',
    insurance_expiration: '', authority_status: ''
  });

  const lookupMCDOT = async () => {
    const dot = formData.dot_number?.trim();
    const mc = formData.mc_number?.trim();
    
    if (!dot && !mc) {
      toast.error('Enter a DOT or MC number first');
      return;
    }

    // Determine search type and value
    let searchType, searchValue;
    if (dot && dot !== '111111') {
      searchType = 'DOT';
      searchValue = dot;
    } else if (mc && mc !== '111111' && !mc.toLowerCase().startsWith('mc-111111')) {
      searchType = 'MC';
      searchValue = mc.replace(/^MC-?/i, '');
    } else if (dot) {
      searchType = 'DOT';
      searchValue = dot;
    } else {
      toast.error('Enter a valid DOT or MC number');
      return;
    }

    setLookupLoading(true);
    setLookupResult(null);
    try {
      const response = await api.get(`/credit-check/${searchType}/${searchValue}`);
      const data = response.data;
      setLookupResult(data);
      
      if (data.found) {
        toast.success('FMCSA data found!');
        // Auto-fill form fields from FMCSA data
        setFormData(prev => ({
          ...prev,
          legal_name: data.legal_name || prev.legal_name,
          dot_number: data.dot_number || prev.dot_number,
          mc_number: data.mc_number || prev.mc_number || '111111',
          phone: data.phone || prev.phone,
          physical_address: data.physical_address || prev.physical_address,
        }));
        
        // If no MC number found, set to 111111 (local only)
        if (!data.mc_number) {
          setFormData(prev => ({ ...prev, mc_number: '111111' }));
          toast.info('No MC authority found — marked as 111111 (Local Only)');
        }
      } else {
        toast.error(data.message || 'No records found');
      }
    } catch (err) {
      toast.error('Lookup failed');
    } finally {
      setLookupLoading(false);
    }
  };

  useEffect(() => {
    loadCarriers();
  }, []);

  const loadCarriers = () => {
    api.get('/carriers')
      .then(res => setCarriers(res.data))
      .catch(() => toast.error('Failed to load carriers'))
      .finally(() => setLoading(false));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    api.post('/carriers', formData)
      .then(() => {
        toast.success('Carrier added successfully');
        setShowForm(false);
        loadCarriers();
        resetForm();
      })
      .catch(() => toast.error('Failed to add carrier'));
  };

  const resetForm = () => {
    setFormData({
      legal_name: '', dba: '', mc_number: '', dot_number: '', ein: '',
      contact_name: '', phone: '', email: '', physical_address: '',
      equipment_types: [], max_weight: 0, home_state: '',
      preferred_lanes: '', factoring_company: '', notes: '',
      insurance_expiration: '', authority_status: ''
    });
  };

  const downloadPDF = (carrierId) => {
    api.get(`/pdf/carrier/${carrierId}`, { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `carrier_${carrierId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('PDF downloaded');
      })
      .catch(() => toast.error('Failed to download PDF'));
  };

  const openEmailClient = (carrier, useGmail = false) => {
    const subject = `Carrier Packet - ${carrier.legal_name}`;
    const body = `Hello ${carrier.legal_name},

Please find your carrier information packet details:

MC Number: ${carrier.mc_number}
DOT Number: ${carrier.dot_number}
Contact: ${carrier.contact_name}
Phone: ${carrier.phone}

To download the full Carrier Packet PDF, please visit:
${window.location.origin}

Thank you!
Urbanvision Logistics LLC`;

    if (useGmail) {
      // Open Gmail web interface
      const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(carrier.email || '')}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.open(gmailUrl, '_blank');
    } else {
      // Open default email client
      const mailtoLink = `mailto:${carrier.email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailtoLink;
    }
  };

  const exportToExcel = () => {
    api.get('/export/carriers', { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'carriers.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Excel exported');
      })
      .catch(() => toast.error('Failed to export'));
  };

  const viewSpreadsheet = () => {
    const ws = XLSX.utils.json_to_sheet(carriers);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Carriers');
    const htmlStr = XLSX.utils.sheet_to_html(ws);
    const newWindow = window.open('');
    newWindow.document.write(`
      <html>
        <head><title>Carriers Spreadsheet</title>
        <style>
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #4a5568; color: white; }
        </style>
        </head>
        <body>${htmlStr}</body>
      </html>
    `);
  };

  return (
    <div className="space-y-6" data-testid="carriers-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Carriers</h1>
          <p className="text-slate-600 mt-1">Manage your carrier database</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={viewSpreadsheet} variant="outline" data-testid="view-spreadsheet-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            View Spreadsheet
          </Button>
          <Button onClick={exportToExcel} variant="outline" data-testid="export-excel-btn">
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          <Dialog open={showForm} onOpenChange={setShowForm}>
            <DialogTrigger asChild>
              <Button data-testid="add-carrier-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Carrier
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Carrier</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm font-semibold text-blue-900 mb-2">FMCSA Lookup</p>
                  <p className="text-xs text-blue-700 mb-3">Enter DOT or MC number below, then click "Lookup" to verify carrier info. If carrier has no MC authority, enter 111111 for MC (Local Only).</p>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div>
                      <Label className="text-xs">DOT Number</Label>
                      <Input value={formData.dot_number} onChange={e => setFormData({...formData, dot_number: e.target.value})} placeholder="e.g. 2462923" data-testid="dot-input" />
                    </div>
                    <div>
                      <Label className="text-xs">MC Number</Label>
                      <Input value={formData.mc_number} onChange={e => setFormData({...formData, mc_number: e.target.value})} placeholder="e.g. MC-849873 or 111111" data-testid="mc-input" />
                    </div>
                  </div>
                  <Button 
                    type="button" 
                    onClick={lookupMCDOT} 
                    disabled={lookupLoading}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="fmcsa-lookup-btn"
                  >
                    {lookupLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking FMCSA...</> : <><Search className="w-4 h-4 mr-2" /> Lookup FMCSA (DOT/MC)</>}
                  </Button>
                  
                  {/* Inline FMCSA Results */}
                  {lookupResult && lookupResult.found && (
                    <div className="mt-3 p-3 bg-white rounded-lg border border-blue-200 space-y-1">
                      <div className="flex items-center gap-2">
                        {lookupResult.risk_level === 'LOW' ? 
                          <CheckCircle className="w-4 h-4 text-green-600" /> : 
                          <AlertTriangle className="w-4 h-4 text-red-600" />}
                        <span className="font-bold text-sm">{lookupResult.legal_name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          lookupResult.risk_level === 'LOW' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>{lookupResult.operating_status}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 text-xs text-slate-600">
                        {lookupResult.entity_type && <p>Type: <strong>{lookupResult.entity_type}</strong></p>}
                        {lookupResult.dot_number && <p>DOT: <strong>{lookupResult.dot_number}</strong></p>}
                        {lookupResult.mc_number && <p>MC: <strong>{lookupResult.mc_number}</strong></p>}
                        {lookupResult.phone && <p>Phone: <strong>{lookupResult.phone}</strong></p>}
                        {lookupResult.authority_status && <p>Authority: <strong>{lookupResult.authority_status}</strong></p>}
                        {lookupResult.physical_address && <p className="col-span-2">Address: <strong>{lookupResult.physical_address}</strong></p>}
                      </div>
                      {!lookupResult.mc_number && (
                        <div className="mt-1 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          No MC authority — carrier is LOCAL ONLY. MC set to 111111.
                        </div>
                      )}
                      {lookupResult.public_url && (
                        <a href={lookupResult.public_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">View full FMCSA record</a>
                      )}
                    </div>
                  )}
                  {lookupResult && !lookupResult.found && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                      {lookupResult.message || 'No FMCSA records found'}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Legal Name *</Label>
                    <Input required value={formData.legal_name} onChange={e => setFormData({...formData, legal_name: e.target.value})} />
                  </div>
                  <div>
                    <Label>DBA</Label>
                    <Input value={formData.dba} onChange={e => setFormData({...formData, dba: e.target.value})} />
                  </div>
                  <div>
                    <Label>MC Number * <span className="text-xs text-slate-400">(111111 = Local Only)</span></Label>
                    <Input required value={formData.mc_number} onChange={e => setFormData({...formData, mc_number: e.target.value})} placeholder="MC-123456 or 111111" />
                    {formData.mc_number === '111111' && (
                      <p className="text-xs text-yellow-600 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> LOCAL ONLY - No MC Authority</p>
                    )}
                  </div>
                  <div>
                    <Label>DOT Number *</Label>
                    <Input required value={formData.dot_number} onChange={e => setFormData({...formData, dot_number: e.target.value})} placeholder="1234567" />
                  </div>
                  <div>
                    <Label>EIN *</Label>
                    <Input required value={formData.ein} onChange={e => setFormData({...formData, ein: e.target.value})} />
                  </div>
                  <div>
                    <Label>Contact Name *</Label>
                    <Input required value={formData.contact_name} onChange={e => setFormData({...formData, contact_name: e.target.value})} />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input required type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input required type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                  </div>
                  <div className="col-span-2">
                    <Label>Physical Address *</Label>
                    <Input required value={formData.physical_address} onChange={e => setFormData({...formData, physical_address: e.target.value})} />
                  </div>
                  <div>
                    <Label>Equipment Types (comma-separated) *</Label>
                    <Input required placeholder="Dry Van, Reefer" value={formData.equipment_types.join(', ')} onChange={e => setFormData({...formData, equipment_types: e.target.value.split(',').map(t => t.trim())})} />
                  </div>
                  <div>
                    <Label>Max Weight (lbs) *</Label>
                    <Input required type="number" value={formData.max_weight} onChange={e => setFormData({...formData, max_weight: parseInt(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Home State *</Label>
                    <Input required value={formData.home_state} onChange={e => setFormData({...formData, home_state: e.target.value})} />
                  </div>
                  <div>
                    <Label>Preferred Lanes</Label>
                    <Input value={formData.preferred_lanes} onChange={e => setFormData({...formData, preferred_lanes: e.target.value})} />
                  </div>
                  <div className="col-span-2">
                    <Label>Notes</Label>
                    <Input value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} />
                  </div>
                  <div>
                    <Label>Insurance Expiration</Label>
                    <Input type="date" value={formData.insurance_expiration} onChange={e => setFormData({...formData, insurance_expiration: e.target.value})} data-testid="form-insurance-exp" />
                  </div>
                  <div>
                    <Label>Authority Status</Label>
                    <Input value={formData.authority_status} onChange={e => setFormData({...formData, authority_status: e.target.value})} placeholder="e.g. Active, Inactive" data-testid="form-authority-status" />
                  </div>
                </div>
                <Button type="submit" className="w-full">Add Carrier</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {carriers.map(carrier => (
          <Card key={carrier.id} className="card-hover" data-testid={`carrier-card-${carrier.id}`}>
            <CardHeader>
              <CardTitle className="text-lg">{carrier.legal_name}</CardTitle>
              <div className="space-y-0.5">
                <p className="text-sm text-slate-500">
                  MC: {carrier.mc_number}
                  {carrier.mc_number === '111111' && (
                    <span className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">LOCAL ONLY</span>
                  )}
                </p>
                {carrier.dot_number && <p className="text-sm text-slate-500">DOT: {carrier.dot_number}</p>}
                {carrier.created_by_pin && <p className="text-xs text-blue-600 font-semibold">Agent: {carrier.created_by_pin}</p>}
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm"><strong>Contact:</strong> {carrier.contact_name}</p>
              <p className="text-sm"><strong>Phone:</strong> {carrier.phone}</p>
              <p className="text-sm"><strong>Email:</strong> {carrier.email}</p>
              <p className="text-sm"><strong>Equipment:</strong> {carrier.equipment_types.join(', ')}</p>
              <div className="flex gap-2 pt-2">
                <Button size="sm" variant="outline" onClick={() => downloadPDF(carrier.id)} data-testid={`download-pdf-${carrier.id}`}>
                  <Download className="w-3 h-3 mr-1" />
                  PDF
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEmailClient(carrier, true)} data-testid={`email-gmail-${carrier.id}`}>
                  <Mail className="w-3 h-3 mr-1" />
                  Gmail
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEmailClient(carrier, false)} data-testid={`email-default-${carrier.id}`}>
                  <Mail className="w-3 h-3 mr-1" />
                  Default
                </Button>
                {user.role === 'super_admin' && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="text-red-600 hover:text-red-700"
                    onClick={() => {
                      if (window.confirm('Delete this carrier? Only master admin can delete.')) {
                        api.delete(`/carriers/${carrier.id}`)
                          .then(() => {
                            toast.success('Carrier deleted');
                            loadCarriers();
                          })
                          .catch(err => toast.error(err.response?.data?.detail || 'Failed to delete'));
                      }
                    }}
                    data-testid={`delete-${carrier.id}`}
                  >
                    Delete
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
